'use strict';

namespace Core {

    class ScTabset {

        // Bound
        title: string;
        activeTabId: string|undefined;
        fullWidth: boolean;
        centered: boolean;
        contained: boolean;
        large: boolean;
        tabsClass: string|undefined;

        private element: JQuery;
        private tabs: ScTab[] = [];
        private activeTab: ScTab;
        private firstTab: ScTab;
        private lastTab: ScTab;

        constructor (
            private $window: ng.IWindowService,
            private broker: Core.Broker,
            private $scope: ng.IScope
        ) {
        }

        public init (element: ng.IAugmentedJQuery): void {
            this.element = element;

            this.firstTab = _.head(this.tabs);
            this.lastTab = _.last(this.tabs);

            this.$scope.$watch('tabset.tabs[0]', (val: ScTab) => {
                if(!this.activeTab && val) {
                    this.selectTab(val);
                }
            });

            // watch "activeTabId" (this can be set programatically)
            this.$scope.$watch('tabset.activeTabId', (val, old) => {
                let tab = angular.isDefined(val)
                    ? _.find(this.tabs, (tab: ScTab) => tab.tabId === this.activeTabId)
                    : this.tabs[0];
                tab && this.selectTab(tab);
            });
        }

        public addTab (tab: ScTab): void {
            this.tabs.push(tab);
        }

        public selectTab(tab: ScTab): void {
            if (this.activeTab) {
                if (tab === this.activeTab) {
                    return;
                }
                this.activeTab.isActive = false;
            }
            tab.isActive = true;
            this.activeTab = tab;
            this.broker.publish('LazySrcRetry');
        }

        public setFocusToTab (tab: ScTab): void {
            tab.element.focus();
        }

        public setFocusToPreviousTab (tab: ScTab): void {
            let next: ScTab =
                tab === this.firstTab
                    ? this.lastTab
                    : this.tabs[this.tabs.indexOf(tab) - 1];
            this.setFocusToTab(next);
        }

        public setFocusToNextTab (tab: ScTab): void {
            let next: ScTab =
                tab === this.lastTab
                    ? this.firstTab
                    : this.tabs[this.tabs.indexOf(tab) + 1];
            this.setFocusToTab(next);
        }

        public setFocusToFirstTab (): void {
            this.setFocusToTab(this.firstTab);
        }

        public setFocusToLastTab (): void {
            this.setFocusToTab(this.lastTab);
        }

        public scrollToView(tab: ScTab): void {
            let tablistScroller: JQuery = this.element.find('.sc-c-tabs__scroller'),
                scrollLeft: number = tablistScroller.scrollLeft(),
                duration: number = 500,
                tabOuterWidth: number = tab.element.outerWidth(),
                tabOffsetLeft: number = tab.element.offset().left;

            if ((tabOuterWidth + tabOffsetLeft) > this.$window.innerWidth) {
                scrollLeft += tabOuterWidth;
            }

            if (tabOffsetLeft < 0) {
                scrollLeft -= tabOuterWidth;
            }

            tablistScroller.animate({ scrollLeft }, duration);
        }
    }

    class ScTab {

        // Bound
        heading: string;
        iconName: string;
        tabId: string;

        public element: JQuery;
        public parentTabset: ScTabset;
        public transcludeFn: ng.ITranscludeFunction;
        public isActive: boolean = false;

        private tabButtonId: string;
        private tabPanelId: string;

        private hasIcon: boolean = false;

        private events = {
            'click': () => {
                this.parentTabset.selectTab(this);
                this.parentTabset.scrollToView(this);
            },
            'keydown': (event) => {
                switch (event.keyCode) {
                    case TR.KeyCode.SPACE:
                    case TR.KeyCode.RETURN:
                        this.parentTabset.selectTab(this);
                        break;
                    case TR.KeyCode.LEFT:
                        this.parentTabset.setFocusToPreviousTab(this);
                        break;
                    case TR.KeyCode.RIGHT:
                        this.parentTabset.setFocusToNextTab(this);
                        break;
                    case TR.KeyCode.HOME:
                        event.preventDefault();
                        this.parentTabset.setFocusToLastTab();
                        break;
                    case TR.KeyCode.END:
                        event.preventDefault();
                        this.parentTabset.setFocusToFirstTab();
                        break;
                    default:
                }
            }
        };

        constructor (
        ) {
        }

        public init (element: ng.IAugmentedJQuery, tabset: ScTabset, transcludeFn: ng.ITranscludeFunction): void {

            this.element = element;
            this.parentTabset = tabset;

            // Add tab to the tabset
            this.parentTabset.addTab(this);

            this.transcludeFn = transcludeFn;

            if (this.iconName) {
                this.hasIcon = true;
            }

            if (this.tabId === undefined) {
                throw 'tabId not specified for ScTab';
            }

            this.tabButtonId = `tab--${this.tabId}`;
            this.tabPanelId = `tabpanel--${this.tabId}`;
        }
    }


    angular
        .module('core')
        .directive('scTabset', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                replace: true,
                transclude: true,
                controller: ScTabset,
                controllerAs: 'tabset',
                bindToController: {
                    title: '@',
                    activeTabId: '@',
                    fullWidth: '@',
                    centered: '@',
                    contained: '@',
                    large: '@',
                    tabsClass: '@'
                },
                template:
                    `<div>
                        <div 
                            class="sc-c-tabs"
                            ng-class="[
                                tabset.tabsClass, {
                                'sc-c-tabs--centered': tabset.centered && !tabset.fullWidth, 
                                'sc-c-tabs--full-width': tabset.fullWidth,
                                'sc-c-tabs--scrollable': !tabset.fullWidth,
                                'sc-c-tabs--contained': tabset.contained, 
                              }]"
                            >
                            <div class="sc-c-tabs__scroller">
                                <div class="sc-c-tabs__tablist"
                                     role="tablist"
                                     id="{{tabset.title.toLowerCase().replace(' ', '-')}}"
                                     ng-attr-aria-label="{{tabset.title || undefined}}"
                                     ng-transclude>
                                </div>
                            </div>
                        </div>
                        <div class="sc-c-tab-panel"
                             ng-repeat="tab in tabset.tabs"
                             role="tabpanel"
                             ng-attr-hidden="{{!tab.isActive || undefined}}"
                             aria-labelledby="{{tab.tabButtonId}}"
                             id="{{tab.tabPanelId}}"
                             tabindex="0"
                             sc-tab-content-transclude="tab">
                        </div>
                    </div>`,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controller: ScTabset
                ) => {
                    controller.init(el);
                }
            };
        })
        .directive('scTab', (): ng.IDirective => {
            return {
                scope: {},
                restrict: 'E',
                require: '^scTabset',
                replace: true,
                transclude: true,
                controller: ScTab,
                controllerAs: 'tab',
                bindToController: {
                    heading: '@',
                    iconName: '@',
                    tabId: '@'
                },
                template:
                    `<button role="tab"
                             class="sc-c-tab"
                             ng-class="{
                                 'sc-c-tab--l': tab.parentTabset.large, 
                                 'sc-c-tab--full-width': tab.parentTabset.fullWidth, 
                                 'is-selected': tab.isActive
                             }"
                             ng-click="tab.events.click($event)"
                             ng-keydown="tab.events.keydown($event)"
                             id="{{tab.tabButtonId}}"
                             aria-selected="{{tab.isActive}}"
                             aria-controls="{{tab.tabPanelId}}"
                             ng-attr-tabindex="{{tab.isActive ? undefined : '-1'}}">
                             
                        <sc-svg-inline 
                            ng-if="tab.hasIcon"
                            name="{{tab.iconName}}"
                            class="sc-c-tab__icon" 
                        />
                        <span class="sc-c-tab__label">{{tab.heading}}</span>
                    </button>
                    `,
                link: (
                    scope : ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    tabsetCtrl: ScTabset,
                    transcludeFn: ng.ITranscludeFunction
                ) => {
                    scope.tab.init(el, tabsetCtrl, transcludeFn);
                }
            };
        })
        .directive('scTabContentTransclude', (): ng.IDirective => {
            return {
                restrict: 'A',
                require: '^scTabset',
                link: (
                    scope: ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes
                ) => {
                    let tab: ScTab = scope.$eval(attr.scTabContentTransclude);
                    tab.transcludeFn(scope, (contents: JQuery) => {
                        angular.forEach(contents, (node: JQuery) => {
                            el.append(node);
                        });
                    });
                }
            };
        });
}

